import {
  belopFraDesimaltekst,
  belopFraTall,
  belopTilDesimaltekst,
  periodiser,
  periodiserFra,
  styrerRapporten,
  tallFraCelle,
  type BudsjettoppgaveDto,
  type Budsjettfase,
  type Meg,
  type Tolv,
} from '@es/shared';
import { formaterBelopFint } from '../../felles/format.js';

/**
 * Budsjettet, slik siden sier det: hvem som ser det, hvilket år siden åpner
 * på, og hvordan et beløp i tusen kroner leses fra et felt og skrives i et.
 */

const SKRIVER = new Set(['leder', 'okonom']);

/**
 * Om budsjettet er noe for kalleren: den som styrer, en kommunalsjef, eller
 * leder eller økonom for en virksomhet.
 */
export function serBudsjettet(meg: Meg): boolean {
  return (
    styrerRapporten(meg) ||
    meg.omradetilganger.some((o) => SKRIVER.has(o.rolle)) ||
    meg.tilganger.some((t) => SKRIVER.has(t.rolle))
  );
}

/**
 * Året siden åpner på: det nyeste budsjettet som finnes, eller neste år —
 * budsjettet lages høsten før.
 */
export function standardar(prosesser: readonly { ar: number }[], idag: Date): number {
  const nyeste = Math.max(...prosesser.map((p) => p.ar));
  return Number.isFinite(nyeste) ? nyeste : idag.getFullYear() + 1;
}

/**
 * Et beløp i tusen kroner, slik det skrives i et felt: «1 200», «-300,5».
 * Et tomt felt er null kroner; null tilbake er at feltet ikke er et tall.
 */
export function lesTusen(tekst: string): string | null {
  const tall = tallFraCelle(tekst);
  if (tall === undefined) return null;
  return belopTilDesimaltekst(belopFraTall(tall ?? 0));
}

/** Et beløp til et felt: uten tusenskille, og uten desimaler når det ikke har noen. */
export function tilFelt(desimaltekst: string): string {
  const belop = belopFraDesimaltekst(desimaltekst);
  const tekst = belopTilDesimaltekst(belop).replace(/\.?0+$/, '');
  return tekst.replace('.', ',');
}

/** Fire felt, ett per år, til fire beløp; null når ett av dem ikke er et tall. */
export function lesFire(felt: readonly string[]): [string, string, string, string] | null {
  const lest = felt.map(lesTusen);
  if (lest.length !== 4 || lest.some((l) => l === null)) return null;
  return lest as [string, string, string, string];
}

/**
 * Feltene i en fordeling med det som ikke er fordelt lagt til, øre for øre.
 * Oversikten viser hele tusen, og et lønns- og prisjustert område har sjelden
 * det: den som skriver av «61 383» fra tabellen, lar 0,35 stå igjen, og
 * budsjettet går ikke opp. Null når ett av feltene ikke er et tall.
 */
export function medResten(felt: readonly string[], udisponert: readonly string[]): string[] | null {
  const lest = lesFire(felt);
  if (lest === null) return null;
  return lest.map((b, i) => tilFelt(sumTekst([b, udisponert[i] ?? '0'])));
}

/**
 * Månedene i et detaljbudsjett lest fra feltene: tolv beløp, eller null når ett
 * av feltene ikke er et tall.
 */
export function lesManeder(felt: readonly string[]): string[] | null {
  const lest = felt.map(lesTusen);
  if (lest.some((l) => l === null)) return null;
  return lest as string[];
}

/**
 * Årsbeløpet fordelt på månedene til felt: likt hver måned, eller som
 * fjorårets budsjett for kontoen når det finnes. Null når årsbeløpet ikke er
 * et tall.
 */
export function periodiserTilFelt(arsbelop: string, fjoraar: readonly string[]): string[] | null {
  const lest = lesTusen(arsbelop);
  if (lest === null) return null;
  const profil = fjoraar.map(belopFraDesimaltekst);
  const harProfil = profil.some((b) => b !== 0n);
  return periodiser(belopFraDesimaltekst(lest), harProfil ? (profil as unknown as Tolv) : null).map(
    (b) => tilFelt(belopTilDesimaltekst(b)),
  );
}

/**
 * En endring for året fordelt likt fra en måned (1–12) og ut året, til felt.
 * Null når beløpet ikke er et tall.
 */
export function periodiserFraTilFelt(arsbelop: string, fraManed: number): string[] | null {
  const lest = lesTusen(arsbelop);
  if (lest === null) return null;
  return periodiserFra(belopFraDesimaltekst(lest), fraManed).map((b) =>
    tilFelt(belopTilDesimaltekst(b)),
  );
}

/** Summen av beløp som desimaltekst. */
export function sumTekst(belop: readonly string[]): string {
  return belopTilDesimaltekst(belop.reduce((a, b) => a + belopFraDesimaltekst(b), 0n));
}

/**
 * Om detaljbudsjettet går opp i rammen, i ord. `avvik` er rammen minus summen
 * av kontoene; null er at virksomheten ikke har fått noen ramme.
 */
export function balansetekst(avvik: string | null): { tekst: string; iBalanse: boolean } {
  if (avvik === null) {
    return { tekst: 'Virksomheten har ikke fått noen ramme ennå.', iBalanse: false };
  }
  const b = belopFraDesimaltekst(avvik);
  const tusen = (x: bigint) =>
    `${formaterBelopFint(belopTilDesimaltekst(x < 0n ? -x : x))} tusen kroner`;
  if (b === 0n) return { tekst: 'I balanse: detaljbudsjettet går opp i rammen.', iBalanse: true };
  if (b > 0n) return { tekst: `${tusen(b)} av rammen er ikke budsjettert ennå.`, iBalanse: false };
  return { tekst: `Budsjettert ${tusen(b)} mer enn rammen.`, iBalanse: false };
}

/** En oppgave i budsjettet, slik Oppgaver på forsiden sier den, og hvor den fører. */
export function budsjettoppgavetekst(o: BudsjettoppgaveDto): {
  tittel: string;
  detalj: string;
  lenke: string;
  lenketekst: string;
} {
  const ar = String(o.ar);
  if (o.oppgave === 'detaljbudsjett') {
    return {
      tittel: `Budsjetter ${o.virksomhet} for ${ar}`,
      detalj: balansetekst(o.ikkeBudsjettert).tekst,
      lenke: `/budsjett?ar=${ar}&virksomhet=${o.virksomhetId}`,
      lenketekst: 'Til detaljbudsjettet',
    };
  }
  const b = belopFraDesimaltekst(o.ikkeFordelt);
  const tusen = `${formaterBelopFint(belopTilDesimaltekst(b < 0n ? -b : b))} tusen kroner`;
  return {
    tittel: `Fordel rammen for ${o.omrade} for ${ar}`,
    detalj:
      b > 0n
        ? `${tusen} er ikke fordelt på virksomhetene.`
        : `Det er fordelt ${tusen} mer enn rammen.`,
    lenke: `/budsjett?ar=${ar}#omrade-${o.omradeId}`,
    lenketekst: 'Til budsjettet',
  };
}

export const FASEFORKLARING: Record<Budsjettfase, string> = {
  rammer: 'Bare økonomisjefen og controllerne ser rammene.',
  innspill:
    'Kommunalsjefene ser rammene for sine områder, foreslår tiltak og fordeler rammen på ' +
    'virksomhetene, som budsjetterer per konto og måned.',
  forslag: 'Tiltakene er låst for områdene. Økonomisjefen innstiller og setter sammen forslaget.',
  vedtatt: 'Budsjettet er vedtatt.',
};
